import React from "react";
import {
	CardinalLocation,
	ClassNameProps,
	compareBooleansDesc,
	compareObjects,
	Conditional,
	Else,
	If,
	List,
	makeClassName,
	Row,
	sort,
	StyleProps,
	toGrammaticalNumber,
	Tooltip,
} from "@hex-insights/core";
import {
	Announcement,
	MediaAttachment,
	Newsletter,
	Post,
	SocialReaction,
	truncateDisplayItems,
	User,
	UserNameFragment,
} from "../../../../Utilities";
import { UserFormat } from "../../../Format";
import { SocialReactionToggleButton } from "../Button";
import styles from "./styles.module.css";

const tooltipSideOrder: CardinalLocation[] = ["bottom", "top", "right", "left"];

export type ReactionsViewProps = {
	announcementID?: Announcement["id"] | null;
	mediaAttachmentID?: MediaAttachment["id"] | null;
	newsletterID?: Newsletter["id"] | null;
	postID?: Post["id"] | null;
	reactions: (Pick<SocialReaction, "id"> & {
		author: UserNameFragment & Pick<User, "isCurrent">;
	})[];
} & Partial<ClassNameProps & StyleProps>;

export function ReactionsView({
	announcementID = null,
	mediaAttachmentID = null,
	newsletterID = null,
	postID = null,
	reactions,
	className,
	style,
}: ReactionsViewProps) {
	const formValues = React.useMemo(
		() => ({ announcementID, mediaAttachmentID, newsletterID, postID }),
		[announcementID, mediaAttachmentID, newsletterID, postID],
	);

	const sortedReactions = React.useMemo(
		() =>
			sort(
				reactions,
				compareObjects((e) => e.author?.isCurrent ?? false, compareBooleansDesc()),
			),
		[reactions],
	);
	const viewerReaction = sortedReactions.find((reaction) => reaction.author?.isCurrent) ?? null;

	const firstReaction = sortedReactions[0];
	const otherReactions = sortedReactions.slice(1);
	const {
		displayedItems: displayedOtherReactions,
		anyHidden: anyOtherReactionsHidden,
		numHidden: numOtherReactionsHidden,
	} = truncateDisplayItems(otherReactions, 8);

	return (
		<Row justify="spaced-start" align="center" className={makeClassName(styles["reactions"], className)} style={style}>
			<SocialReactionToggleButton instanceID={viewerReaction?.id} formValues={formValues} />{" "}
			<Conditional>
				<If condition={sortedReactions.length === 0}>
					<span>Be the first to like this</span>
				</If>
				<Else>
					<span>
						Liked by
						<span className={styles["person-name"]} style={{ margin: "0 0.25rem" }}>
							{firstReaction?.author ? UserFormat.name(firstReaction.author) : ""}
						</span>
						<If condition={otherReactions.length > 0}>
							<wbr />
							and
							<wbr />
							<Tooltip.Container sideOrder={tooltipSideOrder} style={{ marginLeft: "0.25rem", display: "inline" }}>
								<Tooltip className={styles["reactions__others-tooltip"]}>
									<Tooltip.Body>
										<List className={styles["reactions__others-list"]}>
											{displayedOtherReactions.map((reaction) => (
												<List.Item key={reaction.id} className="nowrap">
													{UserFormat.name(reaction.author)}
												</List.Item>
											))}
											<If condition={anyOtherReactionsHidden}>
												<span className="nowrap">
													And {toGrammaticalNumber("Other", numOtherReactionsHidden, true)}...
												</span>
											</If>
										</List>
									</Tooltip.Body>
								</Tooltip>

								<span className={styles["person-name"]}>
									{sortedReactions.length - 1}&nbsp;{toGrammaticalNumber("other", sortedReactions.length - 1)}
								</span>
							</Tooltip.Container>
						</If>
					</span>
				</Else>
			</Conditional>
		</Row>
	);
}
